import React, { useState } from 'react';
import { X, Loader2, Eye } from 'lucide-react';
import SmartImage from './SmartImage';

type ImageSourceMode = 'file' | 'url';

interface ImageSourceTabsProps {
    // Renders whichever box matches the active tab — 'file' for the usual click-to-upload
    // box, 'url' for the paste-URL box. Pass the same content each call re-renders.
    children: (mode: ImageSourceMode) => React.ReactNode;
    // Hides the "Paste URL" tab entirely (and always renders the 'file' child) when the
    // caller hasn't wired up a URL handler for this tile.
    enableUrl?: boolean;
    className?: string;
    // Fires whenever the admin switches tabs (in either direction) — callers use this to
    // clear whatever was loaded under the previous tab, so switching source never leaves a
    // stale image/URL behind.
    onModeChange?: (mode: ImageSourceMode) => void;
}

// Two-tab toggle — "Image Upload" / "Paste URL" — placed under an upload field's label so
// an admin can pick a local file or paste an already-hosted image URL instead.
export const ImageSourceTabs: React.FC<ImageSourceTabsProps> = ({ children, enableUrl = true, className, onModeChange }) => {
    const [mode, setMode] = useState<ImageSourceMode>('file');

    const selectMode = (m: ImageSourceMode) => {
        if (m === mode) return;
        setMode(m);
        onModeChange?.(m);
    };

    if (!enableUrl) return <>{children('file')}</>;

    return (
        <div className={`space-y-1.5 ${className || ''}`}>
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                <button
                    type="button"
                    onClick={() => selectMode('file')}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-colors ${mode === 'file' ? 'bg-white text-[#1B4332] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Image Upload
                </button>
                <button
                    type="button"
                    onClick={() => selectMode('url')}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold transition-colors ${mode === 'url' ? 'bg-white text-[#1B4332] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Paste URL
                </button>
            </div>
            {children(mode)}
        </div>
    );
};

interface PasteUrlBoxProps {
    // The tile's current image URL (however it was set) — previewed here via SmartImage.
    url?: string;
    onSubmit: (url: string) => void;
    // Fires when the X button is pressed — callers use this to remove the loaded image,
    // not just the typed text.
    onClear?: () => void;
    className?: string;
    previewClassName?: string;
    placeholder?: string;
    // True while the caller is still fetching/uploading the pasted URL (that can take a
    // while — a slow-to-fetch remote image, Firebase re-upload, etc.) — shows a spinner in
    // place of the input/Load button and the preview instead of leaving the admin looking at
    // an unresponsive box with no feedback.
    loading?: boolean;
    // Opens a larger view of the loaded image (e.g. a popup) — same affordance the "Image
    // Upload" tab already gets once a file is uploaded. Omit to leave the preview static.
    onView?: (url: string) => void;
}

// The "Paste URL" tab's content — a text input + Load button, previewing the pasted image
// via SmartImage (the shared image-by-URL renderer) once loaded. The typed URL stays in
// the input after loading (it isn't cleared out from under the admin) — an X button lets
// them explicitly clear the text and the loaded image together.
export const PasteUrlBox: React.FC<PasteUrlBoxProps> = ({ url, onSubmit, onClear, className, previewClassName, placeholder = 'Paste image URL', loading, onView }) => {
    const [value, setValue] = useState('');

    const submit = () => {
        const trimmed = value.trim();
        if (!trimmed || loading) return;
        onSubmit(trimmed);
    };

    const clear = () => {
        setValue('');
        onClear?.();
    };

    return (
        <div className={`space-y-2 ${className || ''}`}>
            <div className="flex items-center gap-1.5">
                <div className="flex-1 min-w-0 relative">
                    <input
                        type="url"
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                        placeholder={placeholder}
                        disabled={loading}
                        className="w-full text-xs pl-2.5 pr-7 py-2 border border-slate-200 rounded-lg outline-none focus:border-[#1B4332] disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    {(value || url) && !loading && (
                        <button
                            type="button"
                            onClick={clear}
                            title="Clear"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    onClick={submit}
                    disabled={loading}
                    className="px-3 py-2 text-[11px] font-bold text-white bg-[#1B4332] hover:bg-[#163829] rounded-lg transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                    {loading ? <Loader2 size={13} className="animate-spin" /> : 'Load'}
                </button>
            </div>
            {loading ? (
                <div className={`flex items-center justify-center gap-2 text-xs text-blue-600 font-medium bg-blue-50 border border-blue-100 rounded-lg ${previewClassName || 'w-full h-32'}`}>
                    <Loader2 size={14} className="animate-spin" />
                    Loading image…
                </div>
            ) : url && (
                <div className="relative">
                    <SmartImage
                        src={url}
                        alt="Preview"
                        className={`${previewClassName || 'w-full h-32 object-cover rounded-lg border border-slate-200 bg-white'} ${onView ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
                        onClick={onView ? () => onView(url) : undefined}
                    />
                    {onView && (
                        <button
                            type="button"
                            onClick={() => onView(url)}
                            title="View full image"
                            className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-white/90 text-[#1B4332] hover:bg-white shadow-sm transition-colors"
                        >
                            <Eye size={14} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
